import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';
const prefetchRoute = (_href: string) => {};
import WhatsAppIcon from '@/components/WhatsAppIcon';

interface NavItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    handleScroll(); // Initial check für korrekten Zustand beim Laden
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navItems: NavItem[] = [
    { label: 'Leistungen', href: '/leistungen' },
    { label: 'Branchen', href: '/branchen' },
    { label: 'Referenzen', href: '/referenzen' },
    { label: 'Ratgeber', href: '/ratgeber' },
    {
      label: 'Agentur',
      href: '/ueber-uns',
      children: [
        { label: 'Unser Team', href: '/ueber-uns' },
        { label: 'Unsere Arbeitsweise', href: '/so-arbeiten-wir' },
        { label: 'Unser Netzwerk', href: '/netzwerk' },
      ],
    },

    { label: 'Kontakt', href: '/kontakt' },
  ];

  const isActive = (href: string) => location.pathname === href;
  const isDropdownActive = (item: NavItem) => 
    item.children?.some(child => location.pathname === child.href) || location.pathname === item.href;
  
  const handlePrefetch = (href: string) => () => prefetchRoute(href);
  const logoBase = '/images/logo';
  const logoSrc = buildSrc(logoBase, 256);
  const logoSrcSet = buildSrcSet(logoBase, IMAGE_WIDTHS.logo);

  const handleDropdownToggle = (label: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setOpenDropdown(prev => prev === label ? null : label);
  };

  return (
    <>
      {/* Skip Link for keyboard navigation - WCAG 2.4.1 */}
      <a href="#main-content" className="skip-link">
        Zum Hauptinhalt springen
      </a>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        aria-label="Hauptnavigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled || isMobileMenuOpen
            ? 'bg-secondary backdrop-blur-xl shadow-lg shadow-black/20 border-b border-white/10' 
            : 'bg-transparent'
        }`}
    >
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group"
            onMouseEnter={handlePrefetch('/')}
            onFocus={handlePrefetch('/')}
            onTouchStart={handlePrefetch('/')}
          >
            <motion.div whileHover={{ scale: 1.02 }} className="flex items-center gap-3">
              {/* Logo Icon with glow effect */}
              <div className="relative">
                <img 
                  src={logoSrc} 
                  srcSet={logoSrcSet}
                  sizes="44px"
                  alt="Enno & Jens – Digital-Agentur Wernigerode" 
                  width={44}
                  height={44}
                  className="w-11 h-11 rounded-lg object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <span className={`font-display font-semibold text-lg transition-colors duration-300 ${
                  isScrolled ? 'text-secondary-foreground' : 'text-white'
                }`}>
                  Enno & Jens
                </span>
                <span className={`block text-xs transition-colors duration-300 ${
                  isScrolled ? 'text-secondary-foreground/70' : 'text-white/70'
                }`}>
                  Agentur Wernigerode
                </span>
              </div>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1" ref={dropdownRef}>
            {navItems.map((item) => (
              item.children ? (
                <div key={item.label} className="relative">
                  <button
                    onClick={() => handleDropdownToggle(item.label)}
                    onMouseEnter={() => {
                      item.children?.forEach(child => prefetchRoute(child.href));
                    }}
                    className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group flex items-center gap-1 ${
                      isScrolled 
                        ? isDropdownActive(item)
                          ? 'text-primary'
                          : 'text-secondary-foreground/80 hover:text-secondary-foreground hover:bg-white/10'
                        : isDropdownActive(item)
                          ? 'text-primary'
                          : 'text-white/80 hover:text-white hover:bg-white/10'
                    }`}
                    aria-expanded={openDropdown === item.label}
                    aria-haspopup="true"
                  >
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    {/* Active indicator */}
                    <motion.div
                      className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary rounded-full"
                      initial={false}
                      animate={{ 
                        scaleX: isDropdownActive(item) ? 1 : 0,
                        opacity: isDropdownActive(item) ? 1 : 0
                      }}
                      transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                    />
                  </button>
                  
                  <AnimatePresence>
                    {openDropdown === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full left-0 mt-2 py-2 bg-card rounded-xl shadow-xl border border-border/50 min-w-[180px] backdrop-blur-xl"
                      >
                        {item.children?.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            onMouseEnter={handlePrefetch(child.href)}
                            onFocus={handlePrefetch(child.href)}
                            className={`block px-4 py-2.5 text-sm font-medium transition-colors ${
                              isActive(child.href)
                                ? 'text-primary bg-primary/10'
                                : 'text-foreground hover:text-primary hover:bg-muted/50'
                            }`}
                            onClick={() => setOpenDropdown(null)}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.href}
                  aria-current={isActive(item.href) ? 'page' : undefined}
                  onMouseEnter={handlePrefetch(item.href)}
                  onFocus={handlePrefetch(item.href)}
                  onTouchStart={handlePrefetch(item.href)}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg group ${
                    isScrolled 
                      ? isActive(item.href)
                        ? 'text-primary'
                        : 'text-secondary-foreground/80 hover:text-secondary-foreground hover:bg-white/10'
                      : isActive(item.href)
                        ? 'text-primary'
                        : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.label}
                  {/* Active indicator - konsistente Animation für alle Links */}
                  <motion.div
                    className="absolute bottom-0 left-4 right-4 h-0.5 bg-primary rounded-full"
                    initial={false}
                    animate={{ 
                      scaleX: isActive(item.href) ? 1 : 0,
                      opacity: isActive(item.href) ? 1 : 0
                    }}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                  />
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="flex items-center gap-3">
            <Link
              to="/kontakt"
              className="hidden md:block"
              onMouseEnter={handlePrefetch('/kontakt')}
              onFocus={handlePrefetch('/kontakt')}
              onTouchStart={handlePrefetch('/kontakt')}
            >
              <motion.span
                className="relative inline-flex items-center px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold text-sm shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-shadow duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative z-10">Projekt starten</span>
              </motion.span>
            </Link>

            {/* WhatsApp quick link – komplett weiße Konturen, Theme-Hintergrund */}
            <a
              href="https://wa.me/message/PP7X2YL2QYUKK1"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp chat starten"
              className={`ml-1 inline-flex items-center justify-center p-2 rounded-lg transition-colors text-white ${
                isScrolled
                  ? 'bg-white/10 hover:bg-white/20'
                  : 'bg-white/10 hover:bg-white/20'
              }`}
            >
              <WhatsAppIcon className="w-5 h-5" />
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
            className={`lg:hidden p-2.5 rounded-xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
              isScrolled 
                ? 'text-secondary-foreground hover:bg-white/10 focus:ring-offset-secondary' 
                : 'text-white hover:bg-white/10 focus:ring-offset-transparent'
            }`}
          >
            <motion.div
              key={isMobileMenuOpen ? "close" : "menu"}
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" aria-hidden="true" />
              ) : (
                <Menu className="w-6 h-6" aria-hidden="true" />
              )}
            </motion.div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.nav
            id="mobile-menu"
            aria-label="Mobile Navigation"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="lg:hidden bg-secondary border-t border-white/10 overflow-hidden"
          >
            <div className="container mx-auto px-6 py-6 space-y-2">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  {item.children ? (
                    <div className="space-y-1">
                      <button
                        onClick={(e) => handleDropdownToggle(item.label, e)}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-lg font-medium transition-all ${
                          isDropdownActive(item)
                            ? 'bg-primary/20 text-primary'
                            : 'text-secondary-foreground hover:bg-white/10'
                        }`}
                        aria-expanded={openDropdown === item.label}
                        type="button"
                      >
                        {item.label}
                        <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {openDropdown === item.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="pl-4 space-y-1 overflow-hidden"
                          >
                            {item.children.map((child) => (
                              <Link
                                key={child.href}
                                to={child.href}
                                className={`flex items-center px-4 py-2.5 rounded-xl text-base font-medium transition-all ${
                                  isActive(child.href)
                                    ? 'bg-primary/20 text-primary'
                                    : 'text-secondary-foreground/80 hover:text-secondary-foreground hover:bg-white/10'
                                }`}
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {child.label}
                                {isActive(child.href) && (
                                  <div className="ml-auto w-2 h-2 rounded-full bg-primary" />
                                )}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={item.href}
                      aria-current={isActive(item.href) ? 'page' : undefined}
                      onMouseEnter={handlePrefetch(item.href)}
                      onFocus={handlePrefetch(item.href)}
                      onTouchStart={handlePrefetch(item.href)}
                      className={`flex items-center px-4 py-3 rounded-xl text-lg font-medium transition-all ${
                        isActive(item.href)
                          ? 'bg-primary/20 text-primary'
                          : 'text-secondary-foreground hover:bg-white/10'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                      {isActive(item.href) && (
                        <div className="ml-auto w-2 h-2 rounded-full bg-primary" />
                      )}
                    </Link>
                  )}
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="pt-4"
              >
                <Link
                  to="/kontakt"
                  className="flex items-center justify-center w-full px-6 py-4 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold shadow-lg shadow-primary/25"
                  onMouseEnter={handlePrefetch('/kontakt')}
                  onFocus={handlePrefetch('/kontakt')}
                  onTouchStart={handlePrefetch('/kontakt')}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Projekt starten
                </Link>

                <a
                  href="https://wa.me/message/PP7X2YL2QYUKK1"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp chat starten"
                  className="mt-3 flex items-center justify-center w-full px-6 py-3 rounded-xl bg-white/10 text-secondary-foreground font-semibold shadow-sm hover:bg-white/20 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  WhatsApp Chat
                </a>
              </motion.div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.nav>
    </>
  );
};

export default Navigation;
